import type { RootContent, Text } from 'mdast'
import type { AstNode } from '../types'

/**
 * 文本块片段（用于渐入动画）
 */
export interface TextChunk {
  /** 文本内容 */
  text: string
  /** 创建时间戳 */
  createdAt: number
}

/**
 * 扩展的文本节点（支持 chunks）
 */
export interface TextNodeWithChunks extends Text {
  /** 稳定部分的长度（不需要动画） */
  stableLength?: number
  /** 临时的文本片段，用于渐入动画 */
  chunks?: TextChunk[]
}

/**
 * 计算 AST 节点的总字符数
 */
export function countChars(node: RootContent): number {
  return countCharsInNode(node as AstNode)
}

/**
 * 计算单个 AST 节点的字符数（内部辅助函数）
 */
function countCharsInNode(n: AstNode): number {
  if (n.value && typeof n.value === 'string') {
    return n.value.length
  }
  if (n.children && Array.isArray(n.children)) {
    let count = 0
    for (const child of n.children) {
      count += countCharsInNode(child)
    }
    return count
  }
  // 其他节点（如 thematicBreak, image）算作 1 个字符
  return 1
}

/**
 * 累积的 chunks 信息
 */
export interface AccumulatedChunks {
  /** 已经稳定显示的字符数（不需要动画） */
  stableChars: number
  /** 累积的 chunk 列表 */
  chunks: TextChunk[]
}

/** chunk 范围信息 */
interface ChunkRange {
  start: number
  end: number
  chunk: TextChunk
}

/**
 * 截断 AST 节点，只保留前 maxChars 个字符
 * 支持 chunks（用于渐入动画）
 * 
 * 性能说明：
 * - 此函数每次调用都会从头遍历 AST 节点
 * - 但由于 BlockTransformer 是按 block 处理的，每次只遍历单个 block 的 AST
 * - 单个 block（如一个段落、一个代码块）通常只有几百个字符，性能开销很小
 * - 对于整个文档的渲染，已完成的 blocks 不会重新遍历
 * 
 * @param node 原始节点（单个 block 的 AST）
 * @param maxChars 最大字符数
 * @param accumulatedChunks 累积的 chunks 信息（用于渐入动画）
 * @returns 截断后的节点，如果 maxChars <= 0 返回 null
 */
export function sliceAst(
  node: RootContent, 
  maxChars: number,
  accumulatedChunks?: AccumulatedChunks
): RootContent | null {
  if (maxChars <= 0) return null

  let remaining = maxChars
  let charIndex = 0
  
  // 计算 chunks 在文本中的范围
  const chunkRanges: ChunkRange[] = []
  if (accumulatedChunks && accumulatedChunks.chunks.length > 0) {
    let chunkStart = accumulatedChunks.stableChars
    for (const chunk of accumulatedChunks.chunks) {
      chunkRanges.push({
        start: chunkStart,
        end: chunkStart + chunk.text.length,
        chunk
      })
      chunkStart += chunk.text.length
    }
  }

  function process(n: AstNode): AstNode | null {
    if (remaining <= 0) return null

    // 文本类节点：截断 value，可能添加 chunks
    if (n.value && typeof n.value === 'string') {
      const nodeStart = charIndex
      const nodeEnd = charIndex + n.value.length
      
      const take = Math.min(n.value.length, remaining)
      remaining -= take
      charIndex = nodeEnd

      const slicedValue = n.value.slice(0, take)
      
      const result: AstNode & { stableLength?: number; chunks?: TextChunk[] } = { 
        ...n, 
        value: slicedValue 
      }
      
      // 检查是否有 chunks 落在这个节点范围内
      if (chunkRanges.length > 0 && accumulatedChunks) {
        const nodeChunks: TextChunk[] = []
        let firstChunkLocalStart = take  // 第一个 chunk 在节点中的起始位置
        
        for (const range of chunkRanges) {
          // 计算 chunk 与当前节点的交集
          const overlapStart = Math.max(range.start, nodeStart)
          const overlapEnd = Math.min(range.end, nodeStart + take)
          
          if (overlapStart < overlapEnd) {
            // 有交集，提取对应的文本（相对于 slicedValue）
            const localStart = overlapStart - nodeStart
            const localEnd = overlapEnd - nodeStart
            const chunkText = slicedValue.slice(localStart, localEnd)
            
            if (chunkText.length > 0) {
              // 记录第一个 chunk 的起始位置
              if (nodeChunks.length === 0) {
                firstChunkLocalStart = localStart
              }
              nodeChunks.push({
                text: chunkText,
                createdAt: range.chunk.createdAt
              })
            }
          }
        }
        
        if (nodeChunks.length > 0) {
          result.stableLength = firstChunkLocalStart
          result.chunks = nodeChunks
        }
      }
      
      return result
    }

    // 容器节点：递归处理 children
    if (n.children && Array.isArray(n.children)) {
      const newChildren: AstNode[] = []
      
      for (const child of n.children) {
        if (remaining <= 0) break
        
        const processed = process(child)
        
        if (processed) {
          newChildren.push(processed)
        }
      }
      
      if (newChildren.length === 0) {
        return null
      }
      return { ...n, children: newChildren }
    }

    // 其他节点（如 thematicBreak, image）
    remaining -= 1
    charIndex += 1
    return { ...n }
  }

  return process(node as AstNode) as RootContent | null
}

/**
 * 追加到 AST：将源节点截断到指定字符数，并与基础节点合并
 *
 * 实现说明：
 * - 完整截断 sourceNode 到 endChars，然后通过 smartMergeAst 复用已有节点引用
 * - 虽然每次都重新遍历，但由于只处理单个 block，性能开销很小
 * - smartMergeAst 确保前面已稳定的子节点引用被复用，减少 React/Vue 的重渲染
 *
 * @param baseNode 已截断的基础节点（稳定的部分）
 * @param sourceNode 原始完整节点（单个 block）
 * @param endChars 结束字符位置（新的进度）
 * @param accumulatedChunks 累积的 chunks 信息（用于渐入动画）
 * @returns 合并后的完整节点
 */
export function appendToAst(
  baseNode: RootContent,
  sourceNode: RootContent,
  endChars: number,
  accumulatedChunks?: AccumulatedChunks
): RootContent {
  // 完整截断到 endChars，保留完整的结构信息
  const fullSlice = sliceAst(sourceNode, endChars, accumulatedChunks)

  // 如果截断失败，返回 baseNode
  if (!fullSlice) {
    return baseNode
  }

  // 智能合并：复用 baseNode 中已有的稳定节点引用
  return smartMergeAst(baseNode, fullSlice)
}

/**
 * 合并 AST 节点：复用已稳定的子节点引用
 *
 * 目的：
 * - 虽然 sliceAst 已经创建了新的节点对象，但通过复用 baseNode 中的子节点引用
 * - 可以让 React/Vue/Svelte 的 diff 算法识别出未变化的部分，减少 DOM 更新
 *
 * 策略：
 * - 容器节点：复用前面已完成的子节点引用，只替换最后一个（正在增长的）子节点
 * - 文本节点：直接使用 fullSlice（因为文本内容在变化）
 * - 这是一个"事后优化"，不减少遍历开销，但减少框架层面的重渲染
 */
function smartMergeAst(baseNode: RootContent, fullSlice: RootContent): RootContent {
  // 类型不同，直接返回 fullSlice
  if (baseNode.type !== fullSlice.type) {
    return fullSlice
  }

  const base = baseNode as AstNode
  const full = fullSlice as AstNode

  // 文本节点：直接使用 fullSlice（chunks 信息在 fullSlice 中）
  if (full.value !== undefined) {
    return fullSlice
  }

  // 容器节点：智能合并 children
  if (base.children && Array.isArray(base.children) && full.children && Array.isArray(full.children)) {
    // 如果 fullSlice 的 children 更少（不应该发生），直接返回 fullSlice
    if (full.children.length < base.children.length) {
      return fullSlice
    }

    // 如果 children 数量相同，只有最后一个 child 可能变化
    if (full.children.length === base.children.length) {
      if (base.children.length === 0) {
        return fullSlice
      }
      // 复用前面的稳定节点，只递归处理最后一个
      const lastIndex = base.children.length - 1
      const mergedLast = smartMergeAst(
        base.children[lastIndex] as RootContent,
        full.children[lastIndex] as RootContent
      )
      return {
        ...full,
        children: [
          ...base.children.slice(0, lastIndex),
          mergedLast as AstNode
        ]
      } as RootContent
    }

    // fullSlice 的 children 更多，说明有新的 child 被添加
    // 复用 base 的前 (base.children.length - 1) 个节点
    // 递归处理 base 的最后一个和 full 对应位置的节点
    // 然后追加 full 的新节点
    const baseLastIndex = base.children.length - 1
    const mergedLast = smartMergeAst(
      base.children[baseLastIndex] as RootContent,
      full.children[baseLastIndex] as RootContent
    )
    return {
      ...full,
      children: [
        ...base.children.slice(0, baseLastIndex),
        mergedLast as AstNode,
        ...full.children.slice(base.children.length)
      ]
    } as RootContent
  }

  // 其他情况，直接返回 fullSlice
  return fullSlice
}

/**
 * 深拷贝 AST 节点
 * 使用递归浅拷贝实现，比 JSON.parse/stringify 更高效
 * 且保持对象结构完整性
 */
export function cloneNode<T extends RootContent>(node: T): T {
  // 优先使用 structuredClone（Node 17+ / 现代浏览器）
  if (typeof structuredClone === 'function') {
    return structuredClone(node)
  }
  
  // 回退到递归拷贝
  return deepClone(node) as T
}

/**
 * 递归深拷贝对象
 */
function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => deepClone(item)) as T
  }

  const cloned = {} as T
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key])
    }
  }
  return cloned
}
